import { useMemo } from 'react';
import { useGoogleLogin } from '@react-oauth/google';

const SCOPE = 'https://www.googleapis.com/auth/classroom.courses https://www.googleapis.com/auth/classroom.rosters https://www.googleapis.com/auth/classroom.coursework.me https://www.googleapis.com/auth/classroom.coursework.students https://www.googleapis.com/auth/classroom.profile.emails';
const GOOGLE_EMAIL_COOKIE_NAME = 'google_last_used_email';
const COOKIE_EXPIRY_DAYS = 365;

const getCookie = (name) => {
    try {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.startsWith(name + '=')) {
                return decodeURIComponent(cookie.substring(name.length + 1));
            }
        }
    } catch (error) {
        console.warn('Failed to read cookie:', error);
    }
    return null;
};

const setCookie = (name, value, days) => {
    try {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax`;
    } catch (error) {
        console.warn('Failed to set cookie:', error);
    }
};

export const getStoredGoogleEmail = () => {
    return getCookie(GOOGLE_EMAIL_COOKIE_NAME);
};

export const storeGoogleEmail = (email) => {
    if (email) {
        setCookie(GOOGLE_EMAIL_COOKIE_NAME, email, COOKIE_EXPIRY_DAYS);
    }
};

export const fetchGoogleUserEmail = async (accessToken) => {
    try {
        const response = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        if (response.ok) {
            const userInfo = await response.json();
            return userInfo.email || null;
        }
    } catch (error) {
        console.warn('Failed to fetch Google user email:', error);
    }
    return null;
};

export const useGoogleAuth = (onSuccess, onSilentError, onInteractiveError) => {
    const storedEmail = getStoredGoogleEmail();

    const googleLogin = useGoogleLogin({
        onSuccess,
        onError: (error) => {
            if (onInteractiveError) {
                onInteractiveError(error);
            }
        },
        onNonOAuthError: (error) => {
            if (error?.type === 'popup_blocked' || error?.type === 'popup_failed_to_open') {
                if (onInteractiveError) {
                    onInteractiveError({ type: 'popup_blocked', error: 'popup_blocked' });
                }
            } else if (error?.type === 'popup_closed') {
                if (onInteractiveError) {
                    onInteractiveError({ type: 'popup_closed', error: 'popup_closed_by_user' });
                }
            } else if (onInteractiveError) {
                onInteractiveError(error);
            }
        },
        scope: SCOPE,
        flow: 'auth-code',
        access_type: 'offline',
    });

    const silentLoginConfig = useMemo(() => {
        const config = {
            onSuccess,
            onError: (error) => {
                if (onSilentError) {
                    onSilentError(error);
                }
            },
            onNonOAuthError: (error) => {
                if (error?.type === 'popup_blocked' || error?.type === 'popup_failed_to_open') {
                    if (onSilentError) {
                        onSilentError({ type: 'popup_blocked', error: 'popup_blocked' });
                    }
                } else if (error?.type === 'popup_closed') {
                    if (onSilentError) {
                        onSilentError({ type: 'popup_closed', error: 'popup_closed_by_user' });
                    }
                } else if (onSilentError) {
                    onSilentError(error);
                }
            },
            scope: SCOPE,
            flow: 'implicit',
            prompt: 'none',
        };
        
        if (storedEmail) {
            config.login_hint = storedEmail;
        }
        
        return config;
    }, [onSuccess, onSilentError, storedEmail]);

    const googleLoginSilent = useGoogleLogin(silentLoginConfig);

    return {
        googleLogin,
        googleLoginSilent,
        scope: SCOPE,
    };
};
